// InboxDrawer — the shell-level inbox (Epic 4 · S4.1).
//
// Available from every page in every workspace — a drawer, not a route. Reads
// v_my_messages (the one-store view; one row per message across email, chat, DM,
// task comment, design feedback — 172,300 messages, transport is metadata).
// Replies write through fn_compose_send via the kit <Composer> — the single write
// path. A blocked send returns the message to draft with the reason in
// metadata.send_blocked; the Composer already surfaces that and keeps the draft.
//
// Agent drafts land proposed and wait for a human — visually distinct, with
// Release + Edit (the Composer's affordances). DMs are visible to two people —
// can('dm.read') is a UI convenience, but the wall is in v_my_messages, so a UI
// mistake cannot leak one. We additionally hide the DM channel in the UI when the
// person lacks dm.read, so we never offer a composer they can't use.
//
// Opening from a mention passes a threadId — the drawer opens focused on that
// thread. Esc closes; scrim closes; focus returns to the trigger.
//
// RPC/view shapes are UNCONFIRMED (see api/shell.js). Renders defensively and
// degrades through <ErrorState> when the view/RPC is missing — a missing object
// is a backend bug to report, never a UI patch (knowledge-base §18).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getMyMessages } from '../../../api/shell';
import { useShellSurface } from '../../../contexts/ShellSurfaceContext';
import { useWorkspace } from '../../../contexts/WorkspaceContext';
import { Composer, ClientSlideOut } from '../kit';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import { formatWhen } from '../../shared/format';

const CHANNEL_LABELS = {
  email: 'Email',
  chat: 'Chat',
  in_app: 'In-app',
  task_comment: 'Task',
  design_feedback: 'Design',
};

// Collapse the UNCONFIRMED column variants onto one shape so the UI never hardens
// against a single field name. Every accessor falls back; a missing field simply
// does not render (knowledge-base §8: render what comes back).
const norm = (m = {}) => ({
  id: m.id ?? m.message_id ?? m.uuid ?? null,
  threadId: m.thread_id ?? m.thread ?? m.conversation_id ?? null,
  channel: m.channel ?? m.type ?? m.kind ?? null,
  is_dm: m.is_dm ?? m.isDm ?? false,
  from: m.from ?? m.sender ?? m.author ?? m.from_name ?? null,
  to: m.to ?? m.recipient ?? m.to_name ?? null,
  subject: m.subject ?? m.title ?? null,
  body: m.body ?? m.text ?? m.content ?? m.snippet ?? null,
  snippet: m.snippet ?? m.preview ?? m.body ?? m.text ?? null,
  at: m.at ?? m.message_timestamp ?? m.sent_at ?? m.created_at ?? m.received_at ?? null,
  isAgentDraft: !!(m.is_agent_draft ?? m.agent_draft ?? m.proposed ?? m.is_proposed),
  seen: m.seen ?? m.is_seen ?? null,
  clientId: m.client_id ?? m.client ?? null,
  clientName: m.client_name ?? m.client ?? null,
  taskId: m.task_id ?? m.task ?? null,
  taskTitle: m.task_title ?? m.task ?? null,
});

const channelLabel = (c) => (c ? CHANNEL_LABELS[c] || c : 'Message');

export default function InboxDrawer() {
  const { inbox, closeInbox, openInbox } = useShellSurface();
  const { can } = useWorkspace();
  const canDm = can('dm.read');

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState('all');
  const [refreshKey, setRefreshKey] = useState(0);

  const open = inbox.open;
  const activeThreadId = inbox.threadId;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMyMessages({ limit: 200 });
      setMessages((res?.data || []).map(norm));
    } catch (err) {
      setError(err?.message || "Couldn't load your inbox.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open. A threadId (from a mention) sets the active thread without a
  // separate fetch — the thread's messages are already in the loaded page.
  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  // Esc closes (only while this drawer is the open surface).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeInbox();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeInbox]);

  // DMs are wall-gated in the view; hide them in the UI too when the person lacks
  // dm.read so we never offer a composer they can't use. is_dm is the boolean on
  // the message (channel is 'chat' for DMs, never 'dm').
  const visible = useMemo(() => {
    let list = messages;
    if (!canDm) list = list.filter((m) => !m.is_dm);
    if (channel !== 'all') list = list.filter((m) => m.channel === channel);
    return list;
  }, [messages, canDm, channel]);

  // Threads are derived from the loaded page — group by threadId. A null threadId
  // (rare; a standalone message) is treated as its own thread keyed by its id.
  const threads = useMemo(() => {
    const map = new Map();
    for (const m of visible) {
      const key = m.threadId ?? m.id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(m);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
    }
    return map;
  }, [visible]);

  const activeThread = activeThreadId ? threads.get(activeThreadId) : null;

  const channelOptions = useMemo(() => {
    const present = new Set(messages.map((m) => m.channel).filter(Boolean));
    const opts = [{ key: 'all', label: 'All' }];
    for (const c of Object.keys(CHANNEL_LABELS)) {
      if (present.has(c)) {
        opts.push({ key: c, label: CHANNEL_LABELS[c] });
      }
    }
    return opts;
  }, [messages]);

  if (!open) return null;

  const onSent = () => setRefreshKey((k) => k + 1);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Inbox">
      <div
        className="absolute inset-0 bg-black/30"
        onClick={closeInbox}
        aria-hidden="true"
      />
      <aside
        className="absolute inset-y-0 right-0 flex w-full flex-col bg-white shadow-xl dark:bg-gray-900 md:w-[560px]"
        data-testid="inbox-drawer"
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          {activeThreadId && (
            <button
              type="button"
              onClick={() => openInbox(null)}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 md:hidden"
              aria-label="Back to inbox"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {activeThreadId ? 'Thread' : 'Inbox'}
          </h2>
          <div className="ml-auto flex items-center gap-2">
            {!activeThreadId && (
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="text-sm rounded-md border border-gray-200 bg-white px-2 py-1 text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                aria-label="Filter by channel"
              >
                {channelOptions.map((o) => (
                  <option key={o.key} value={o.key}>{o.label}</option>
                ))}
              </select>
            )}
            <button
              type="button"
              onClick={closeInbox}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Close inbox"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body — list, or a thread + composer */}
        <div className="flex-1 overflow-hidden">
          {error ? (
            <div className="p-4">
              <ErrorState variant="card" message={error} onRetry={load} />
            </div>
          ) : loading ? (
            <div className="p-4 text-sm text-gray-400">Loading…</div>
          ) : activeThreadId && activeThread ? (
            <ThreadView thread={activeThread} onSent={onSent} canDm={canDm} />
          ) : activeThreadId && !activeThread ? (
            <EmptyState variant="inset" message="This thread has no messages you can see." padding="p-8" />
          ) : visible.length === 0 ? (
            <EmptyState variant="inset" message="No messages. Empty is correct — a client with nothing to say is a client you served." padding="p-8" />
          ) : (
            <MessageList threads={threads} onOpen={(id) => openInbox(id)} />
          )}
        </div>
      </aside>
    </div>
  );
}

// ── List ────────────────────────────────────────────────────────────────────
function MessageList({ threads, onOpen }) {
  // Order threads by their most recent message.
  const ordered = useMemo(() => {
    const arr = [...threads.entries()];
    arr.sort(([, a], [, b]) => new Date(b[0]?.at || 0) - new Date(a[0]?.at || 0));
    return arr;
  }, [threads]);

  return (
    <ul className="divide-y divide-gray-100 overflow-y-auto dark:divide-gray-800" data-testid="inbox-list">
      {ordered.map(([threadId, msgs]) => {
        const last = msgs[0] || {};
        return (
          <li key={threadId}>
            <button
              type="button"
              onClick={() => onOpen(threadId)}
              className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <span className="mt-0.5 shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                {channelLabel(last.channel)}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                    {last.from || last.clientName || 'Unknown'}
                  </span>
                  {last.isAgentDraft && (
                    <span className="shrink-0 rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                      Proposed
                    </span>
                  )}
                  <span className="ml-auto shrink-0 text-xs text-gray-400 tabular-nums">
                    {formatWhen(last.at)}
                  </span>
                </span>
                <span className="mt-0.5 block truncate text-sm text-gray-500 dark:text-gray-400">
                  {last.subject ? `${last.subject} — ` : ''}{last.snippet || last.body || ''}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ── Thread view ─────────────────────────────────────────────────────────────
function ThreadView({ thread, onSent, canDm }) {
  const first = thread[0] || {};
  const isDm = first.is_dm === true;
  // The composer is gated by dm.read for DM threads — the wall is in the view, but
  // we never offer a composer the person can't use.
  const canCompose = !isDm || canDm;

  // Chronological (oldest first) for reading; the list was sorted newest-first.
  const ordered = useMemo(
    () => [...thread].sort((a, b) => new Date(a.at || 0) - new Date(b.at || 0)),
    [thread],
  );

  return (
    <div className="flex h-full flex-col" data-testid="inbox-thread">
      <div className="flex-1 overflow-y-auto px-4 py-3">
        {first.clientName && first.clientId && (
          <div className="mb-3">
            <ClientSlideOut clientId={first.clientId} clientName={first.clientName} />
          </div>
        )}
        <ul className="space-y-3">
          {ordered.map((m, i) => (
            <li
              key={m.id || i}
              className={`rounded-lg border px-3 py-2 ${
                m.isAgentDraft
                  ? 'border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-900/20'
                  : 'border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {m.from || 'Unknown'}
                </span>
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                  {channelLabel(m.channel)}
                </span>
                {m.isAgentDraft && (
                  <span className="rounded bg-violet-100 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                    Proposed
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-400 tabular-nums">
                  {formatWhen(m.at)}
                </span>
              </div>
              {m.subject && (
                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{m.subject}</p>
              )}
              <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                {m.body || m.snippet || ''}
              </p>
            </li>
          ))}
        </ul>
      </div>

      {canCompose ? (
        <div className="border-t border-gray-200 p-3 dark:border-gray-800">
          <Composer
            channel={isDm ? 'chat' : (first.channel || 'email')}
            threadId={first.threadId}
            clientId={first.clientId}
            taskId={first.taskId}
            isAgentDraft={false}
            isDm={isDm}
            onSent={onSent}
          />
        </div>
      ) : (
        <div className="border-t border-gray-200 p-3 text-xs text-gray-400 dark:border-gray-800">
          You don&rsquo;t have access to this DM thread.
        </div>
      )}
    </div>
  );
}


